/**
 * Dados da empresa (17 §3.4): registro único usado na proposta. Só o admin acessa (menu e RLS).
 * CNPJ, CPF e CEP são gravados só com dígitos; a tela mostra formatado.
 */
import { useEffect, useState, type ChangeEvent } from "react";
import {
  Edit,
  Loading,
  SaveButton,
  SimpleForm,
  TextInput,
  Title,
  Toolbar,
  email,
  regex,
  required,
  useGetList,
  useInput,
  useNotify,
} from "react-admin";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import UploadIcon from "@mui/icons-material/UploadFileOutlined";
import { cnpjValido, cpfValido, formatarCep, formatarCnpj, formatarCpf, soDigitos } from "@sobral/dominio";
import { supabase } from "../lib/supabase";
import { EntradaTelefone } from "../componentes";
import { ContasBancarias } from "./contas-bancarias";

const BUCKET = "empresa";
const TAMANHO_MAXIMO = 2 * 1024 * 1024; // igual ao limite do bucket na migração
const TIPOS_ACEITOS = ["image/png", "image/jpeg"];

const validarCnpj = (valor: unknown) => (cnpjValido(String(valor ?? "")) ? undefined : "CNPJ inválido.");
const validarCpf = (valor: unknown) =>
  valor === null || valor === undefined || valor === "" || cpfValido(String(valor)) ? undefined : "CPF inválido.";
const validarCep = regex(/^\d{8}$/, "CEP precisa ter 8 dígitos.");
const validarUf = regex(/^[A-Z]{2}$/, "UF com 2 letras maiúsculas (ex.: SE).");
const vazioParaNulo = (valor: unknown) => (valor === "" ? null : valor);
const digitosOuNulo = (valor: unknown) => {
  const d = soDigitos(String(valor ?? ""));
  return d === "" ? null : d;
};

/** Imagem da assinatura: envia ao bucket privado e grava o caminho no formulário. */
function EntradaAssinatura() {
  const notify = useNotify();
  const { field } = useInput({ source: "assinatura_caminho", parse: vazioParaNulo });
  const caminho = (field.value as string | null) ?? null;
  const [urlPrevia, setUrlPrevia] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    let ativo = true;
    if (!caminho) {
      setUrlPrevia(null);
      return;
    }
    void supabase.storage
      .from(BUCKET)
      .createSignedUrl(caminho, 300)
      .then(({ data, error }) => {
        if (!ativo) return;
        setUrlPrevia(error ? null : data.signedUrl);
      });
    return () => {
      ativo = false;
    };
  }, [caminho]);

  async function enviar(evento: ChangeEvent<HTMLInputElement>) {
    const arquivo = evento.target.files?.[0];
    evento.target.value = "";
    if (!arquivo) return;
    if (!TIPOS_ACEITOS.includes(arquivo.type)) {
      notify("Use uma imagem PNG ou JPG.", { type: "error" });
      return;
    }
    if (arquivo.size > TAMANHO_MAXIMO) {
      notify("A imagem precisa ter no máximo 2 MB.", { type: "error" });
      return;
    }
    setEnviando(true);
    // Cada envio é um arquivo novo: a assinatura anterior fica guardada (histórico).
    const extensao = arquivo.type === "image/png" ? "png" : "jpg";
    const novoCaminho = `assinatura/${new Date().toISOString().replace(/[:.]/g, "-")}.${extensao}`;
    const { error } = await supabase.storage.from(BUCKET).upload(novoCaminho, arquivo, {
      contentType: arquivo.type,
      upsert: false,
    });
    setEnviando(false);
    if (error) {
      notify(`Falha ao enviar a imagem: ${error.message}`, { type: "error" });
      return;
    }
    field.onChange(novoCaminho);
    notify("Imagem enviada. Clique em Salvar para gravar.", { type: "info" });
  }

  return (
    <Stack spacing={1} sx={{ width: "100%" }}>
      <Typography variant="subtitle2" color="text.secondary">
        Assinatura do representante (PNG ou JPG, até 2 MB; fundo transparente fica melhor na proposta)
      </Typography>
      <Box
        sx={{
          border: "1px dashed",
          borderColor: "divider",
          borderRadius: 1,
          minHeight: 96,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 1,
          bgcolor: "background.paper",
        }}
      >
        {urlPrevia ? (
          <Box component="img" src={urlPrevia} alt="Assinatura" sx={{ maxHeight: 120, maxWidth: "100%" }} />
        ) : (
          <Typography variant="body2" color="text.disabled">
            {caminho ? "Carregando…" : "Nenhuma assinatura enviada"}
          </Typography>
        )}
      </Box>
      <Box>
        <Button component="label" variant="outlined" startIcon={<UploadIcon />} disabled={enviando}>
          {enviando ? "Enviando…" : caminho ? "Trocar imagem" : "Enviar imagem"}
          <input type="file" hidden accept={TIPOS_ACEITOS.join(",")} onChange={enviar} />
        </Button>
      </Box>
    </Stack>
  );
}

function Secao({ titulo }: { titulo: string }) {
  return (
    <Typography variant="h6" sx={{ mt: 2, mb: 1, fontSize: "1rem" }}>
      {titulo}
    </Typography>
  );
}

function BarraSalvar() {
  return (
    <Toolbar>
      <SaveButton />
    </Toolbar>
  );
}

const meia = { xs: 12, sm: 6 } as const;
const terco = { xs: 12, sm: 4 } as const;

function FormularioEmpresa() {
  return (
    <SimpleForm toolbar={<BarraSalvar />}>
      <Alert severity="info" sx={{ mb: 1, width: "100%" }}>
        Estes dados saem no cabeçalho e no rodapé das propostas. Toda alteração fica registrada na auditoria.
      </Alert>
      <Grid container columnSpacing={2} sx={{ width: "100%" }}>
        <Grid size={12}>
          <Secao titulo="Identificação" />
        </Grid>
        <Grid size={meia}>
          <TextInput source="razao_social" validate={required()} fullWidth />
        </Grid>
        <Grid size={meia}>
          <TextInput source="marca" parse={vazioParaNulo} fullWidth />
        </Grid>
        <Grid size={terco}>
          <TextInput source="cnpj" format={formatarCnpj} parse={soDigitos} validate={[required(), validarCnpj]} fullWidth />
        </Grid>
        <Grid size={terco}>
          <TextInput source="inscricao_estadual" parse={vazioParaNulo} fullWidth />
        </Grid>
        <Grid size={terco}>
          <TextInput source="nire" parse={vazioParaNulo} fullWidth />
        </Grid>

        <Grid size={12}>
          <Secao titulo="Endereço e contato" />
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
          <TextInput source="logradouro" validate={required()} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextInput source="numero" validate={required()} fullWidth />
        </Grid>
        <Grid size={meia}>
          <TextInput source="complemento" parse={vazioParaNulo} fullWidth />
        </Grid>
        <Grid size={meia}>
          <TextInput source="bairro" validate={required()} fullWidth />
        </Grid>
        <Grid size={terco}>
          <TextInput source="municipio" validate={required()} fullWidth />
        </Grid>
        <Grid size={terco}>
          <TextInput
            source="uf"
            parse={(v: string) => v.toUpperCase().slice(0, 2)}
            validate={[required(), validarUf]}
            fullWidth
          />
        </Grid>
        <Grid size={terco}>
          <TextInput source="cep" format={formatarCep} parse={soDigitos} validate={[required(), validarCep]} fullWidth />
        </Grid>
        <Grid size={terco}>
          <EntradaTelefone source="telefone" fullWidth />
        </Grid>
        <Grid size={terco}>
          <TextInput source="email" parse={vazioParaNulo} validate={email("E-mail inválido.")} fullWidth />
        </Grid>
        <Grid size={terco}>
          <TextInput source="site" parse={vazioParaNulo} fullWidth />
        </Grid>

        <Grid size={12}>
          <Secao titulo="Representante legal" />
        </Grid>
        <Grid size={terco}>
          <TextInput source="representante_nome" parse={vazioParaNulo} fullWidth />
        </Grid>
        <Grid size={terco}>
          <TextInput
            source="representante_cpf"
            format={formatarCpf}
            parse={digitosOuNulo}
            validate={validarCpf}
            fullWidth
          />
        </Grid>
        <Grid size={terco}>
          <TextInput source="representante_cargo" parse={vazioParaNulo} fullWidth />
        </Grid>
        <Grid size={12}>
          <EntradaAssinatura />
        </Grid>

      </Grid>
    </SimpleForm>
  );
}

/** Lista do recurso "empresa": abre direto a ficha do registro único. */
export function DadosEmpresa() {
  const { data, isPending, error } = useGetList("empresa", { pagination: { page: 1, perPage: 1 } });
  if (isPending) return <Loading />;
  const registro = data?.[0];
  if (error || !registro) {
    return (
      <Box sx={{ mt: 2 }}>
        <Title title="Dados da empresa" />
        <Alert severity="error">Não foi possível carregar os dados da empresa.</Alert>
      </Box>
    );
  }
  return (
    <>
      <Edit resource="empresa" id={registro.id} title="Dados da empresa" mutationMode="pessimistic" redirect={false} actions={false}>
        <FormularioEmpresa />
      </Edit>
      <ContasBancarias />
    </>
  );
}
